/**
 * Unified Analytics Interface
 *
 * Provides a clean API for tracking events throughout the application
 *
 * ⚠️ CRITICAL: NEVER track patient data or PII
 * Only track:
 * - Feature usage (aggregate)
 * - Performance metrics
 * - Business operations (anonymized)
 * - Error rates
 */

import { mixpanelAnalytics } from "./mixpanel";

interface TrackEventParams {
  category: string;
  action: string;
  label?: string;
  value?: number;
  metadata?: Record<string, unknown>;
}

interface TrackPerformanceParams {
  metricName: string;
  duration: number;
  metadata?: Record<string, unknown>;
}

class Analytics {
  /**
   * Initialize analytics
   * Call this in main.tsx
   */
  initialize() {
    mixpanelAnalytics.initialize();
  }

  /**
   * Track page view
   * @param pageName - Name of the page (e.g., 'Consultation', 'Patient List')
   * @param metadata - Additional metadata (NO PII!)
   *
   * @example
   * analytics.trackPageView('Consultation', { section: 'notes' });
   */
  trackPageView(pageName: string, metadata?: Record<string, unknown>) {
    mixpanelAnalytics.trackPageView(pageName, metadata);
  }

  /**
   * Track feature usage
   * @param feature - Feature name (e.g., 'Consultation', 'Pharmacy')
   * @param action - Action performed (e.g., 'Save Draft', 'Dispense')
   * @param metadata - Additional metadata (NO PII!)
   *
   * @example
   * analytics.trackFeature('Consultation', 'Save Draft', {
   *   auto_save: true,
   *   editor_type: 'rich_text'
   * });
   */
  trackFeature(feature: string, action: string, metadata?: Record<string, unknown>) {
    mixpanelAnalytics.track(`${feature}: ${action}`, {
      feature,
      action,
      ...metadata,
    });
  }

  /**
   * Track custom event
   * @param params - Event parameters
   *
   * @example
   * analytics.trackEvent({
   *   category: 'Pharmacy',
   *   action: 'Dispense',
   *   label: 'Batch Dispense',
   *   value: 5,
   *   metadata: { dispense_type: 'batch' }
   * });
   */
  trackEvent({ category, action, label, value, metadata }: TrackEventParams) {
    mixpanelAnalytics.track(`${category}: ${action}`, {
      category,
      action,
      label,
      value,
      ...metadata,
    });
  }

  /**
   * Track performance metric
   * @param params - Performance parameters
   *
   * @example
   * analytics.trackPerformance({
   *   metricName: 'consultation.save.duration',
   *   duration: 1234,
   *   metadata: { item_count: 5 }
   * });
   */
  trackPerformance({ metricName, duration, metadata }: TrackPerformanceParams) {
    mixpanelAnalytics.track("Performance Metric", {
      metric_name: metricName,
      duration_ms: Math.round(duration),
      ...metadata,
    });
  }

  /**
   * Track error (non-sensitive info only)
   * @param errorType - Type of error
   * @param errorMessage - Error message (sanitized, no PII)
   * @param metadata - Additional metadata
   *
   * @example
   * analytics.trackError('API Error', 'Failed to save', {
   *   endpoint: '/api/consultation',
   *   status_code: 500
   * });
   */
  trackError(errorType: string, errorMessage: string, metadata?: Record<string, unknown>) {
    mixpanelAnalytics.track("Error", {
      error_type: errorType,
      error_message: errorMessage,
      ...metadata,
    });
  }

  /**
   * Identify user (use anonymized/hashed ID only!)
   * @param userId - Anonymized user ID (NOT email, NOT name)
   * @param properties - User properties (role, type, etc. - NO PII!)
   *
   * @example
   * analytics.identifyUser('user_abc123', {
   *   role: 'doctor',
   *   clinic_type: 'outpatient'
   * });
   */
  identifyUser(userId: string, properties?: Record<string, unknown>) {
    mixpanelAnalytics.identify(userId);

    if (properties) {
      mixpanelAnalytics.setUserProperties(properties);
    }
  }

  /**
   * Set user properties (NO PII!)
   * @param properties - User properties
   *
   * @example
   * analytics.setUserProperties({
   *   role: 'doctor',
   *   clinic_type: 'outpatient'
   * });
   */
  setUserProperties(properties: Record<string, unknown>) {
    mixpanelAnalytics.setUserProperties(properties);
  }

  /**
   * Increment user property counter
   * @param property - Property name
   * @param value - Increment value
   *
   * @example
   * analytics.incrementUserProperty('consultations_completed', 1);
   */
  incrementUserProperty(property: string, value = 1) {
    mixpanelAnalytics.incrementUserProperty(property, value);
  }

  /**
   * Start timing an event
   * @param eventName - Event name to time
   *
   * @example
   * analytics.startTiming('Consultation Save');
   * // ... do work ...
   * analytics.trackFeature('Consultation', 'Save'); // Will include duration
   */
  startTiming(eventName: string) {
    mixpanelAnalytics.timeEvent(eventName);
  }

  /**
   * Set global properties (sent with every event)
   * @param properties - Properties to set globally
   *
   * @example
   * analytics.setGlobalProperties({
   *   clinic_id: 'clinic_123',
   *   user_role: 'doctor'
   * });
   */
  setGlobalProperties(properties: Record<string, unknown>) {
    mixpanelAnalytics.registerSuperProperties(properties);
  }

  /**
   * Clear user data (call on logout)
   */
  clearUser() {
    mixpanelAnalytics.reset();
  }

  /**
   * Opt user out of tracking
   */
  optOut() {
    mixpanelAnalytics.optOut();
  }

  /**
   * Opt user in to tracking
   */
  optIn() {
    mixpanelAnalytics.optIn();
  }

  /**
   * Check if user has opted out
   */
  hasOptedOut(): boolean {
    return mixpanelAnalytics.hasOptedOut();
  }

  /**
   * Get current user ID
   */
  getUserId(): string | undefined {
    return mixpanelAnalytics.getDistinctId();
  }
}

// Export singleton instance
export const analytics = new Analytics();

// Export Mixpanel instance for advanced usage
export { mixpanelAnalytics };
